import { getItem, removeItem, setItem } from "./consent-storage";
import {
  get as getCookie,
  remove as removeCookie,
  set as setCookie,
} from "./cookies";
import { captureException } from "./posthog";
import { CATEGORIES, hasCategory, watchCategory } from "./termly";

// Parse current URL
const url = new URL(window.location.href);

const UTM_PARAMS = ["source", "medium", "campaign", "content", "term"];

// UTM values carried by this navigation itself (query parameters, or the
// Google-referrer heuristic). Current-request data, not storage, so they
// flow to every consumer regardless of consent category (MTN-1444).
const landing = {
  campaign: null,
  content: null,
  medium: null,
  source: null,
  term: null,
};

// Populated at import so a click that beats init() (audit state 12) still
// carries this navigation's own values from memory.
try {
  if (document.referrer) {
    const referrerURL = new URL(document.referrer);

    if (referrerURL.host.includes("google")) {
      landing.source = "google";
    }
  }
} catch (error) {
  console.error(error);
  captureException(error, { source: "utm:referrer-source" });
}

for (const param of UTM_PARAMS) {
  if (url.searchParams.has(`utm_${param}`)) {
    const rawValue = url.searchParams.get(`utm_${param}`);

    try {
      landing[param] = decodeURIComponent(rawValue);
    } catch {
      // Not double-encoded after all — keep the once-decoded value.
      landing[param] = rawValue;
    }
  }
}

// Persisted UTMs split by sink (MTN-1444 disposition):
//   * mtn-utm-* cookies — Advertising; cross-page affiliate attribution for
//     Tune-link decoration and the PHP short-link redirect.
//   * mtn-newsletter-utm localforage record — Analytics; newsletter signup
//     attribution (7-day envelope TTL in the storage adapter).
const NEWSLETTER_UTM_KEY = "mtn-newsletter-utm";

let didInit = false;
let recordPromise;

// Read any advertising click IDs from the URL or their cookies. `landing`
// marks values carried by this navigation itself — the only ones the
// restricted transport below may pass. `recovered` marks cookie-sourced
// Google IDs for the google_attribution_recovered signal.
const readParamOrCookie = (name) => {
  const landingValue = url.searchParams.get(name);

  return landingValue
    ? { landing: true, value: landingValue }
    : { landing: false, value: getCookie(`_${name}`) };
};

const advertisingClickIds = [
  ...["fbclid", "fbc", "fbp", "msclkid", "sncid"].map((name) => ({
    name,
    ...readParamOrCookie(name),
  })),
  ...["gclid", "gbraid", "wbraid"].map((name) => {
    const read = readParamOrCookie(name);

    return { name, ...read, recovered: !read.landing && Boolean(read.value) };
  }),
];

const landingClickIds = advertisingClickIds.filter((id) => id.landing);

// Params this module added, per link, so a state change (revocation, or a
// different transport at the next click) can strip exactly what we added
// and never touch author-written values. Entries for links removed from
// the DOM linger until revocation clears them — small objects, page
// lifetime; switch to per-link data attributes if that ever matters.
const decorations = new Map();

const TUNE_TRANSPORT = Object.freeze({
  CLEAN: "clean",
  FULL: "full",
  RESTRICTED: "restricted",
});

/**
 * Which attribution may ride a Tune /out/ navigation right now (MTN-1447):
 *
 * - full: Termly resolved with Advertising allowed — landing and
 *   cookie-recovered values pass (the normal flow).
 * - restricted: Advertising not allowed (denied, or Termly unresolved) on
 *   a US region profile — only this page's own click IDs and UTMs pass,
 *   held in memory and placed once on the first-party /out/ GET; the edge
 *   strips them before the outbound hop.
 * - clean: everywhere else (EEA/UK profile, or no region information) —
 *   no ad identifiers ride at all.
 *
 * Region comes from Termly's `initialized` region profile
 * (window.termlyRegionSettings, stashed by head.blade.php); a missing
 * profile fails closed to clean. do_not_sell needs no branch here: Termly
 * couples it with declining every non-essential category, so it lands in
 * restricted, navigation continues, and the server reads the opt-out from
 * the consent envelope (enforcement is server-side).
 */
const tuneTransport = () => {
  if (hasCategory(CATEGORIES.ADVERTISING)) {
    return TUNE_TRANSPORT.FULL;
  }

  return window.termlyRegionSettings?.region === "us"
    ? TUNE_TRANSPORT.RESTRICTED
    : TUNE_TRANSPORT.CLEAN;
};

const clear = async () => {
  for (const param of UTM_PARAMS) {
    removeCookie(`mtn-utm-${param}`);
  }

  recordPromise = undefined;
  await removeItem(NEWSLETTER_UTM_KEY);
};

/**
 * Persist the landing UTMs into their category-owned stores. Each write
 * waits for its category's grant, so a consent given moments after the
 * landing still captures the attribution.
 */
const save = async () => {
  const values = {};

  for (const param of UTM_PARAMS) {
    if (landing[param]) {
      values[param] = landing[param];
    }
  }

  if (Object.keys(values).length === 0) {
    return;
  }

  const termly = await import("./termly");

  termly.allowAdvertising(() => {
    for (const [param, value] of Object.entries(values)) {
      // 7-day affiliate-attribution window (MTN-1434 disposition). Host-only:
      // Tune-link decoration and the PHP short-link redirect both read these
      // on the page's own host.
      setCookie(`mtn-utm-${param}`, value, { days: 7 });
    }
  });

  termly.allowAnalytics(async () => {
    // Same-page readers see the post-save record at once; the adapter write
    // lands in the background.
    recordPromise = Promise.resolve(values);

    try {
      await setItem(NEWSLETTER_UTM_KEY, values);
    } catch (error) {
      console.error(error);
    }
  });
};

export const init = async () => {
  // Only init once
  if (didInit) {
    return;
  }

  didInit = true;

  // Drop the saved parameters when this URL carries any UTM parameter of its own
  if (UTM_PARAMS.some((param) => url.searchParams.has(`utm_${param}`))) {
    await clear();
  }

  await save();
};

const addAdvertisingClickIdParams = (linkUrl, clickIds) => {
  const added = [];

  for (const clickId of clickIds) {
    if (clickId.value && !linkUrl.searchParams.has(clickId.name)) {
      linkUrl.searchParams.set(clickId.name, clickId.value);
      added.push(clickId);
    }
  }

  return added;
};

const trackDecoration = (link, addition) => {
  const decoration = decorations.get(link) ?? {
    addedMarker: false,
    clickIds: [],
    utmParams: [],
  };

  decorations.set(link, {
    addedMarker: addition.addedMarker ?? decoration.addedMarker,
    clickIds: [...decoration.clickIds, ...(addition.clickIds ?? [])],
    utmParams: [...decoration.utmParams, ...(addition.utmParams ?? [])],
  });
};

// Remove exactly what this module added to the link, leaving
// author-written parameters alone.
const stripTrackedParams = (link, linkUrl) => {
  const decoration = decorations.get(link);

  if (!decoration) {
    return;
  }

  for (const clickId of decoration.clickIds) {
    if (linkUrl.searchParams.get(clickId.name) === clickId.value) {
      linkUrl.searchParams.delete(clickId.name);
    }
  }

  if (
    decoration.addedMarker &&
    linkUrl.searchParams.get("google_attribution_recovered") === "1"
  ) {
    linkUrl.searchParams.delete("google_attribution_recovered");
  }

  for (const [name, value] of decoration.utmParams) {
    if (linkUrl.searchParams.get(name) === value) {
      linkUrl.searchParams.delete(name);
    }
  }

  decorations.delete(link);
};

const clearDecorations = () => {
  for (const link of decorations.keys()) {
    const linkUrl = new URL(link.href);
    stripTrackedParams(link, linkUrl);
    link.href = linkUrl.href;
  }
};

/**
 * Check if URL already has UTM parameters
 */
const hasExistingUtmParams = (linkUrl) =>
  UTM_PARAMS.some((param) => linkUrl.searchParams.has(`utm_${param}`));

/**
 * Add UTM parameters to URL, reporting what was added so it can be
 * stripped again on a state change.
 */
const addUtmParams = (linkUrl, utmValues) => {
  const added = [];

  for (const param of UTM_PARAMS) {
    if (utmValues[param]) {
      linkUrl.searchParams.set(`utm_${param}`, utmValues[param]);
      added.push([`utm_${param}`, utmValues[param]]);
    }
  }

  return added;
};

/**
 * UTM values for Tune-link decoration and Ledger measurement bodies: this
 * navigation's own values, plus cookie-recovered values only while
 * Advertising is allowed (a denied or unresolved category recovers
 * nothing — MTN-1444). Ledger requests carry no cookies (MTN-1456), so
 * these ride in the request body instead.
 */
export const advertisingUtmValues = () => {
  const advertisingAllowed = hasCategory(CATEGORIES.ADVERTISING);
  const values = {};

  for (const param of UTM_PARAMS) {
    values[param] =
      landing[param] ??
      (advertisingAllowed ? (getCookie(`mtn-utm-${param}`) ?? null) : null);
  }

  return values;
};

export const decorateTuneLinkClickIds = (event) => {
  const link = event.target?.closest?.("a.tune");

  if (!link) {
    return;
  }

  try {
    const linkUrl = new URL(link.href);
    // Re-classify on every click (audit state 12): strip whatever an
    // earlier state added, then decorate for the transport in force now.
    linkUrl.searchParams.delete("google_attribution_recovered");
    stripTrackedParams(link, linkUrl);

    const transport = tuneTransport();

    if (transport !== TUNE_TRANSPORT.CLEAN) {
      const addedClickIds = addAdvertisingClickIdParams(
        linkUrl,
        transport === TUNE_TRANSPORT.FULL
          ? advertisingClickIds
          : landingClickIds
      );

      const addedMarker = addedClickIds.some((clickId) => clickId.recovered);

      if (addedMarker) {
        linkUrl.searchParams.set("google_attribution_recovered", "1");
      }

      const utmParams = hasExistingUtmParams(linkUrl)
        ? []
        : addUtmParams(linkUrl, advertisingUtmValues());

      if (addedClickIds.length > 0 || addedMarker || utmParams.length > 0) {
        trackDecoration(link, {
          addedMarker,
          clickIds: addedClickIds,
          utmParams,
        });
      }
    }

    link.href = linkUrl.href;
  } catch (error) {
    console.error(error);
    captureException(error, { source: "utm:decorate-click-ids" });
  }
};

/**
 * Decorate a single link with UTM parameters
 */
const decorateLink = (link, utmValues) => {
  try {
    const linkUrl = new URL(link.href);

    if (!hasExistingUtmParams(linkUrl)) {
      const utmParams = addUtmParams(linkUrl, utmValues);

      if (utmParams.length > 0) {
        trackDecoration(link, { utmParams });
      }
    }

    link.href = linkUrl.href;
  } catch (error) {
    console.error(error);
    captureException(error, { source: "utm:decorate-link" });
  }
};

export const initTuneLinkClickIdHandler = () => {
  // Installed before Termly resolves, so a pre-resolve click classifies
  // against the live transport instead of losing this page's own IDs
  // (audit state 12). On revocation every tracked decoration is stripped;
  // links a lower transport still allows re-decorate on their next click,
  // and rendered UTMs come back through decorateLink right away.
  document.addEventListener("click", decorateTuneLinkClickIds, true);

  const stopWatching = watchCategory(CATEGORIES.ADVERTISING, (allowed) => {
    if (allowed) {
      return;
    }

    const links = [...decorations.keys()];
    clearDecorations();

    const utmValues =
      tuneTransport() === TUNE_TRANSPORT.CLEAN ? {} : advertisingUtmValues();

    for (const link of links) {
      decorateLink(link, utmValues);
    }
  });

  return () => {
    document.removeEventListener("click", decorateTuneLinkClickIds, true);
    stopWatching();
  };
};

export const decorateLinks = async (element) => {
  const mtn = await import("./mtn");
  mtn.ready(async () => {
    if (!didInit) {
      await init();
    }

    const links = [...element.querySelectorAll("a.tune")];

    if (links.length === 0) {
      return;
    }

    // Clean transport renders undecorated links; a click re-classifies
    // against whatever state holds by then.
    const utmValues =
      tuneTransport() === TUNE_TRANSPORT.CLEAN ? {} : advertisingUtmValues();

    for (const link of links) {
      decorateLink(link, utmValues);
    }
  });
};

// Newsletter-attribution readers: this navigation's own value, else the
// Analytics-gated record (the adapter returns nothing when Analytics is
// denied or the 7-day window has passed).
const readNewsletterUtm = async () => {
  try {
    return await getItem(NEWSLETTER_UTM_KEY);
  } catch {
    return null;
  }
};

const savedValue = async (param) => {
  recordPromise ??= readNewsletterUtm();

  const record = await recordPromise;

  return record?.[param] ?? null;
};

export const source = async () =>
  landing.source ?? (await savedValue("source"));

export const medium = async () =>
  landing.medium ?? (await savedValue("medium"));

export const campaign = async () =>
  landing.campaign ?? (await savedValue("campaign"));

export const content = async () =>
  landing.content ?? (await savedValue("content"));

export const term = async () => landing.term ?? (await savedValue("term"));
